import uniqueId from 'lodash/uniqueId';
import { QUESTION_TYPES } from '@/views/question/constants';

// --- Interfaces ---

export interface ISubQuestionCondition {
  id: string;
  logical_operator: 'AND' | 'OR';
  comparison: string;
  // answer_value can be null for is_empty/is_not_empty comparisons
  answer_value: string | string[] | number | number[] | null;
  target_question_id: number | string;
}

export interface ISubQuestionConfig {
  sub_question_ids: Array<number | string>;
  conditions: ISubQuestionCondition[];
  level: number;
  answer_mapping?: Record<string, Array<number | string>>;
}

// --- Constants ---

export const LOGICAL_OPERATORS = [
  { name: 'かつ', value: 'AND' },
  { name: 'または', value: 'OR' },
];

export const COMPARISON_TYPES = [
  { name: '一致する (is)', value: 'is' },
  { name: '一致しない (not_is)', value: 'not_is' },
  { name: 'いずれかを含む (in)', value: 'in' },
  { name: 'いずれも含まない (not_in)', value: 'not_in' },
  { name: '次の文字を含む (contains)', value: 'contains' },
  { name: '次の文字を含まない (not_contains)', value: 'not_contains' },
  { name: 'より大きい (gt)', value: 'gt' },
  { name: '以上 (gte)', value: 'gte' },
  { name: '未満 (lt)', value: 'lt' },
  { name: '以下 (lte)', value: 'lte' },
  { name: '範囲内 (between)', value: 'between' },
  { name: '正規表現 (regex)', value: 'regex' },
  { name: '未回答 (is_empty)', value: 'is_empty' },
  { name: '回答あり (is_not_empty)', value: 'is_not_empty' },
  { name: '選択数が等しい (count_eq)', value: 'count_eq' },
  { name: '選択数が超える (count_gt)', value: 'count_gt' },
  { name: '選択数が未満 (count_lt)', value: 'count_lt' },
];

/**
 * Returns filtered comparison types based on question type.
 * Spec 3.2.2
 */
export const getComparisonTypesByType = (type: string) => {
  const common = ['is_empty', 'is_not_empty'];

  switch (type) {
    case 'radio':
      // Radio is single-select only (enforced in QuestionListEdit.vue).
      // Only is/not_is operators are needed; in/not_in are redundant for single values.
      return COMPARISON_TYPES.filter((op) =>
        ['is', 'not_is', ...common].includes(op.value),
      );
    case 'pulldown':
      return COMPARISON_TYPES.filter((op) =>
        ['is', 'not_is', 'in', 'not_in', ...common].includes(op.value),
      );
    case 'checkbox':
      return COMPARISON_TYPES.filter((op) =>
        ['in', 'not_in', 'count_eq', 'count_gt', 'count_lt', ...common].includes(op.value),
      );
    case 'input':
    case 'textarea':
      return COMPARISON_TYPES.filter((op) =>
        [
          'is',
          'not_is',
          'contains',
          'not_contains',
          'gt',
          'gte',
          'lt',
          'lte',
          'between',
          'regex',
          ...common,
        ].includes(op.value),
      );
    default:
      return COMPARISON_TYPES;
  }
};


export const DEFAULT_LEVEL = 1;
export const MAX_LEVEL = 5;

export const LEVEL_OPTIONS = Array.from({ length: MAX_LEVEL }, (_, i) => ({
  name: `レベル ${i + 1}`,
  value: i + 1,
}));

// --- Helpers ---

export const PREFIX_CONDITION_LOCAL_ID = 'cond_';

export const getConditionLocalId = (): string => uniqueId(PREFIX_CONDITION_LOCAL_ID);

export const createDefaultCondition = (targetQuestionId: number | string): ISubQuestionCondition => ({
  id: getConditionLocalId(),
  logical_operator: 'AND',
  comparison: 'is',
  answer_value: '',
  target_question_id: targetQuestionId,
});

export const createDefaultSubQuestionConfig = (): ISubQuestionConfig => ({
  sub_question_ids: [],
  conditions: [],
  level: DEFAULT_LEVEL,
  answer_mapping: {},
});

export type CompoundLogic = 'AND' | 'OR';

export const normalizeCompoundLogic = (logic: unknown): CompoundLogic =>
  (typeof logic === 'string' && logic.toUpperCase() === 'OR' ? 'OR' : 'AND');

/**
 * Resolves compound logic from the parent's global conditions (modal state).
 * The connector between rules is stored on the second global row (index 1).
 */
export const resolveCompoundLogicFromGlobal = (
  globalConditions: ISubQuestionCondition[],
): CompoundLogic => {
  if (!globalConditions || globalConditions.length <= 1) {
    return 'AND';
  }
  return normalizeCompoundLogic(globalConditions[1].logical_operator);
};

export interface MapDisplayConditionsOptions {
  logic?: CompoundLogic;
}

const POSITIVE_ANSWER_COMPARISONS = ['is', 'in'];
const NEGATIVE_ANSWER_COMPARISONS = ['not_is', 'not_in'];

/** Normalizes condition answer_value (string | string[] from checkbox in/not_in) to string keys. */
export const normalizeConditionAnswerValues = (answerValue: unknown): string[] => {
  if (answerValue === null || answerValue === undefined || answerValue === '') {
    return [];
  }
  if (Array.isArray(answerValue)) {
    return answerValue.map((v) => String(v));
  }
  return [String(answerValue)];
};

export const conditionAnswerValuesMatch = (left: unknown, right: unknown): boolean => {
  const leftValues = normalizeConditionAnswerValues(left);
  const rightValues = normalizeConditionAnswerValues(right);
  if (leftValues.length !== rightValues.length) {
    return false;
  }
  return leftValues.every((value, index) => value === rightValues[index]);
};

const appendUniqueMappingId = (
  mapping: Record<string, Array<number | string>>,
  answerKey: string,
  childId: number | string,
): void => {
  if (!mapping[answerKey]) {
    mapping[answerKey] = [];
  }
  if (!mapping[answerKey].some((sid) => String(sid) === String(childId))) {
    mapping[answerKey].push(childId);
  }
};

export const hasExplicitGlobalConditionForAnswer = (
  globalConditions: ISubQuestionCondition[],
  answerValue: string,
): boolean =>
  globalConditions.some(
    (gc) =>
      normalizeConditionAnswerValues(gc.answer_value).includes(String(answerValue))
      && POSITIVE_ANSWER_COMPARISONS.includes(gc.comparison),
  );

/**
 * True when a rule only exists to show this child for a per-answer mapping shortcut,
 * not as a user-defined global condition in the modal.
 */
export const isMappingOnlyRule = (
  rule: Pick<ISubQuestionCondition, 'comparison' | 'answer_value'>,
  answerMapping: Record<string, Array<number | string>> | undefined,
  childId: number | string,
  globalConditions: ISubQuestionCondition[],
): boolean => {
  if (!POSITIVE_ANSWER_COMPARISONS.includes(rule.comparison)) {
    return false;
  }
  if (hasExplicitGlobalConditionForAnswer(globalConditions, String(rule.answer_value))) {
    return false;
  }
  const mappedIds = answerMapping?.[String(rule.answer_value)] || [];
  return (
    mappedIds.length > 0
    && mappedIds.some((id) => String(id) === String(childId))
  );
};

export interface BuildChildDisplayConditionsParams {
  childId: number | string;
  globalConditions: ISubQuestionCondition[];
  answerMapping: Record<string, Array<number | string>>;
  defaultOperator: 'is' | 'in';
  parentType?: string;
}

const MAPPING_COMPARISONS = ['is', 'in', 'not_is', 'not_in'];

/**
 * Resolves operator/value for a per-answer (+) mapping from global modal rules when present.
 */
export const resolveMappingRuleForAnswerKey = (
  answerKey: string,
  globalConditions: ISubQuestionCondition[],
  defaultOperator: 'is' | 'in',
): { comparison: string; answer_value: string | string[] | number | number[] | null } => {
  const globalRule = globalConditions.find(
    (gc) =>
      MAPPING_COMPARISONS.includes(gc.comparison)
      && normalizeConditionAnswerValues(gc.answer_value).includes(answerKey),
  );

  if (globalRule) {
    return {
      comparison: globalRule.comparison,
      answer_value: globalRule.answer_value,
    };
  }

  return { comparison: defaultOperator, answer_value: answerKey };
};

/**
 * Builds display_conditions for one sub-question without polluting global modal state.
 * Mapping-only answers use a single rule; global modal rules use filtered compound rules.
 */
export const buildChildDisplayConditions = ({
  childId,
  globalConditions,
  answerMapping,
  defaultOperator,
  parentType,
}: BuildChildDisplayConditionsParams): ReturnType<typeof mapDisplayConditionsToBackend> => {
  const mapping = answerMapping || {};

  const triggerAnswers = Object.keys(mapping).filter((ansValue) =>
    (mapping[ansValue] || []).some((sid) => String(sid) === String(childId)),
  );

  const filteredGlobalConditions = globalConditions.filter((gc) => {
    if (
      POSITIVE_ANSWER_COMPARISONS.includes(gc.comparison)
      || NEGATIVE_ANSWER_COMPARISONS.includes(gc.comparison)
    ) {
      return normalizeConditionAnswerValues(gc.answer_value).some((ansValue) =>
        (mapping[ansValue] || []).some((sid) => String(sid) === String(childId)),
      );
    }
    return true;
  });

  const globalLogic = resolveCompoundLogicFromGlobal(globalConditions);

  // For radio-type parents, logic is always OR because radio is single-select.
  // Each answer (A, B, C) is an alternative trigger — sub-question displays if the
  // user selects ANY answer. Other types (text, checkbox) keep the user-selected
  // globalLogic to avoid breaking existing behavior.
  const effectiveLogic: CompoundLogic = parentType === 'radio' ? 'OR' : globalLogic;

  const childIsMappedToAnswers = triggerAnswers.length > 0;
  // Bug #12: radio + per-answer (+) mapping — each child must only receive rules
  // for its assigned answers. Skip shared compound when mapped; checkbox AND Case 1 unchanged.
  const skipSharedCompoundForMappedRadio =
    parentType === 'radio' && childIsMappedToAnswers;

  // AND modal: all sub-questions share the compound (test_cases.md Case 1).
  // OR + no mapping: when user sets OR conditions via modal without per-answer mapping,
  // filteredGlobalConditions becomes empty — use all global conditions to preserve OR.
  // Support for text-type questions: when parent has no answer_mapping (always empty
  // for input/textarea) and 1+ global conditions exist, use them to build display_conditions.
  // This preserves conditions for text-type sub-questions.
  const useSharedGlobalCompound =
    !skipSharedCompoundForMappedRadio
    && (
      (globalConditions.length >= 2 && globalLogic === 'AND')
      || (Object.keys(mapping).length === 0 && globalConditions.length >= 1)
    );

  if (useSharedGlobalCompound) {
    return mapDisplayConditionsToBackend(globalConditions, { logic: effectiveLogic });
  }

  if (filteredGlobalConditions.length > 0) {
    // When filtering reduces to a single condition, omit the logic option so the output
    // uses the simple { operator, value } form instead of the compound { logic, rules } form.
    // The compound form is only meaningful when there are 2+ rules.
    if (filteredGlobalConditions.length === 1) {
      return mapDisplayConditionsToBackend(filteredGlobalConditions);
    }
    return mapDisplayConditionsToBackend(filteredGlobalConditions, {
      logic: effectiveLogic,
    });
  }

  const mappingOnlyAnswers = triggerAnswers.filter(
    (ans) => !hasExplicitGlobalConditionForAnswer(globalConditions, ans),
  );

  if (mappingOnlyAnswers.length === 0) {
    return null;
  }

  // Bug #10: Per-answer (+) mapping alone is insufficient — admin must set conditions
  // via the parent modal. Never auto-generate display_conditions from answer_mapping alone.
  if (globalConditions.length === 0) {
    return null;
  }

  // Only generate conditions for mapping-only answers that have an explicit rule in
  // globalConditions (is/in/not_is/not_in). Answers absent from globalConditions were
  // never configured by the admin — auto-generating a condition for them would override
  // the admin's choice to leave them without a condition.
  const validMappingOnlyAnswers = mappingOnlyAnswers.filter((ans) =>
    globalConditions.some(
      (gc) =>
        MAPPING_COMPARISONS.includes(gc.comparison)
        && normalizeConditionAnswerValues(gc.answer_value).includes(ans),
    ),
  );

  if (validMappingOnlyAnswers.length === 0) {
    return null;
  }

  if (validMappingOnlyAnswers.length === 1) {
    const resolved = resolveMappingRuleForAnswerKey(
      validMappingOnlyAnswers[0],
      globalConditions,
      defaultOperator,
    );
    return {
      operator: resolved.comparison,
      value: resolved.answer_value,
    };
  }

  const mappingOnlyConditions = validMappingOnlyAnswers.map((ans) => {
    const resolved = resolveMappingRuleForAnswerKey(ans, globalConditions, defaultOperator);
    const c = createDefaultCondition(childId);
    c.answer_value = resolved.answer_value;
    c.comparison = resolved.comparison;
    c.logical_operator = effectiveLogic;
    return c;
  });

  return mapDisplayConditionsToBackend(mappingOnlyConditions, { logic: effectiveLogic });
};

/**
 * Maps frontend conditions array to backend display_conditions JSON format.
 * Spec 3.2.4
 *
 * When auto-generated mapped conditions are prepended, pass `options.logic` from
 * `resolveCompoundLogicFromGlobal(globalConditions)` so user-selected OR/AND is preserved.
 */
export const mapDisplayConditionsToBackend = (
  conditions: ISubQuestionCondition[],
  options?: MapDisplayConditionsOptions,
) => {
  if (!conditions || conditions.length === 0) {
    return null;
  }

  if (conditions.length === 1) {
    // Preserve OR explicitly: if caller passes logic='OR', emit compound form so
    // mapDisplayConditionsFromBackend can restore OR on the next load cycle.
    if (options?.logic && options.logic !== 'AND') {
      return {
        logic: options.logic,
        rules: [{ operator: conditions[0].comparison, value: conditions[0].answer_value }],
      };
    }
    return {
      operator: conditions[0].comparison,
      value: conditions[0].answer_value,
    };
  }

  const logic = options?.logic ?? resolveCompoundLogicFromGlobal(conditions);

  return {
    logic,
    rules: conditions.map((c) => ({
      operator: c.comparison,
      value: c.answer_value,
    })),
  };
};

/**
 * Maps backend display_conditions JSON back to frontend ISubQuestionCondition[].
 */
export const mapDisplayConditionsFromBackend = (displayConditions: any): ISubQuestionCondition[] => {
  if (!displayConditions) {
    return [];
  }

  // Case: Compound condition { logic, rules }
  if (displayConditions.logic && Array.isArray(displayConditions.rules)) {
    const logic = normalizeCompoundLogic(displayConditions.logic);
    return displayConditions.rules.map((rule: any, index: number) => ({
      id: getConditionLocalId(),
      // Index 0 keeps 'AND' per display convention (first row has no visible operator).
      // Index 1+ carries the actual inter-rule logic so resolveCompoundLogicFromGlobal
      // can read it correctly on the next open.
      logical_operator: index === 0 ? 'AND' : logic,
      comparison: rule.operator,
      answer_value: rule.value,
      target_question_id: '', // To be populated during flattening
    }));
  }

  // Case: Single rule { operator, value }
  if (displayConditions.operator) {
    const logic = normalizeCompoundLogic(displayConditions.logic);
    return [{
      id: getConditionLocalId(),
      logical_operator: logic,
      comparison: displayConditions.operator,
      answer_value: displayConditions.value,
      target_question_id: '', // To be populated during flattening
    }];
  }

  return [];
};

export const shouldShowConditionModal = (parentType: string): boolean => {
  const { RADIO, CHECKBOX, INPUT, TEXTAREA } = QUESTION_TYPES;
  if (['pulldown'].includes(parentType)) {
    return false;
  }
  return [RADIO, CHECKBOX, INPUT, TEXTAREA].includes(parentType);
};

/** test_cases.md Case 2: không hiển thị 表示プレビュー */
export const shouldShowMappingPreview = (_parentType: string): boolean => false;

export const shouldShowQuestionLevel = (_parentType: string): boolean => true;

/**
 * Expected API depth for direct children of this parent (0 = root question).
 */
export const getExpectedSubQuestionDepth = (parent: {
  depth?: number;
  sub_question_config?: ISubQuestionConfig;
}): number => {
  const parentDepth =
    typeof parent.depth === 'number'
      ? parent.depth
      : Math.max(0, (parent.sub_question_config?.level ?? 1) - 1);
  return Math.min(parentDepth + 1, MAX_LEVEL - 1);
};

const isChildInAnswerMapping = (
  childId: number | string,
  answerMapping: Record<string, Array<number | string>> | undefined,
): boolean =>
  Object.values(answerMapping || {}).some((ids) =>
    (ids || []).some((id) => String(id) === String(childId)),
  );

/**
 * Client-side guard before save: subs tied to an answer via (+) mapping must produce display_conditions.
 * Unmapped subs in sub_question_ids only (Case 2) may omit conditions.
 */
export const validateSubQuestionsBeforeSave = (
  questions: Array<{
    id: number | string;
    question?: string;
    type?: string;
    sub_question_config?: ISubQuestionConfig;
  }>,
): string[] => {
  const errors: string[] = [];

  questions.forEach((parent) => {
    const config = parent.sub_question_config;
    if (!config?.sub_question_ids?.length) {
      return;
    }

    const { CHECKBOX } = QUESTION_TYPES;
    const defaultOperator = parent.type === CHECKBOX ? 'in' : 'is';
    const mapping = config.answer_mapping || {};
    const globalConditions = config.conditions || [];

    config.sub_question_ids.forEach((childId) => {
      if (!isChildInAnswerMapping(childId, mapping)) {
        return;
      }

      // [SCOPE] Bug #10: mapped subs may omit display_conditions until parent modal has rules.
      if (globalConditions.length === 0) {
        return;
      }

      // Mapping-only children (all trigger answers absent from globalConditions) are not
      // required to have display_conditions — the admin chose not to configure conditions
      // for those answers. Skip validation so saving is allowed without an error.
      const triggerAnswers = Object.keys(mapping).filter(
        (ans) => (mapping[ans] || []).some((id) => String(id) === String(childId)),
      );
      const isMappingOnly =
        triggerAnswers.length > 0
        && triggerAnswers.every((ans) => !hasExplicitGlobalConditionForAnswer(globalConditions, ans));
      if (isMappingOnly) {
        return;
      }

      const built = buildChildDisplayConditions({
        childId,
        globalConditions,
        answerMapping: mapping,
        defaultOperator,
        parentType: parent.type,
      });

      if (built === null) {
        const child = questions.find((q) => String(q.id) === String(childId));
        const label = child?.question || String(childId);
        errors.push(`「${label}」の表示条件を設定してください（選択肢のマッピング）`);
      }
    });
  });

  return errors;
};

/**
 * Resolves a candidate sub-question's depth for the selection modal (graph-first, legacy level fallback).
 */
export const resolveSelectableSubQuestionDepth = (
  questionId: number | string,
  allQuestions: Array<{ id: number | string; sub_question_config?: ISubQuestionConfig }>,
): number => {
  const fromGraph = computeDepthFromGraph(questionId, allQuestions);
  if (findParentInGraph(questionId, allQuestions)) {
    return fromGraph;
  }
  const q = allQuestions.find((item) => String(item.id) === String(questionId));
  return Math.max(0, (q?.sub_question_config?.level ?? 1) - 1);
};

export const shouldShowRadioMappingHint = (parentType: string): boolean => {
  const { RADIO } = QUESTION_TYPES;
  return [RADIO, 'pulldown'].includes(parentType);
};

/** Copy hiển thị trên SubQuestionSection */
export const SUB_QUESTION_RADIO_HINT =
  '各選択肢の＋からサブ質問を設定してください';

export const currentSubQuestionUiFlags = (parentType: string) => ({
  showMappingPreview: shouldShowMappingPreview(parentType),
  showConditionModal: shouldShowConditionModal(parentType),
  showQuestionLevel: shouldShowQuestionLevel(parentType),
  showHint: shouldShowRadioMappingHint(parentType),
  hintText: SUB_QUESTION_RADIO_HINT,
});

/** Kỳ vọng theo test_cases.md (đồng bộ với implementation sau sửa) */
export const expectedSubQuestionUiFlags = currentSubQuestionUiFlags;

export interface MappingPreviewRow {
  optionLabel: string;
  subTitles: string[];
}

export const buildMappingPreviewRows = (params: {
  options: Array<{ value: string }>;
  answerMapping: Record<string, Array<number | string>>;
  allQuestions: Array<{ id?: number | string; temp_id?: string; question: string }>;
}): MappingPreviewRow[] => {
  const { options, answerMapping, allQuestions } = params;
  const titleById = new Map(
    allQuestions.map((q) => [String(q.id ?? q.temp_id), q.question || '']),
  );

  return options.map((opt) => {
    const key = String(opt.value);
    const ids = answerMapping[key] || [];
    return {
      optionLabel: key,
      subTitles: ids
        .map((id) => titleById.get(String(id)) || '')
        .filter(Boolean),
    };
  });
};

const findParentInGraph = (
  questionId: number | string,
  allQuestions: Array<{ id: number | string; sub_question_config?: ISubQuestionConfig }>,
) =>
  allQuestions.find((p) =>
    (p.sub_question_config?.sub_question_ids || []).some(
      (sid) => String(sid) === String(questionId),
    ),
  );

// Sentinel stored in memo while a node is being visited — breaks circular reference loops.
const DEPTH_COMPUTING = -1;

/**
 * Depth in API payload (0 = root). Walks the parent chain via sub_question_ids.
 * Capped at MAX_LEVEL - 1. Does not rely on stale depth fields on parents.
 *
 * Cycle-safe: marks each node as DEPTH_COMPUTING (-1) before recursing so that
 * a circular reference (A → B → A) is detected on re-entry and treated as depth 0
 * instead of causing a RangeError: Maximum call stack size exceeded.
 */
/**
 * Resolves API depth on save. Active sub-questions (in parent graph) use max(graph, level).
 * Unlinked questions use admin-selected level only (Bug #5).
 */
export const resolveSaveDepth = (
  questionId: number | string,
  level: number,
  allQuestions: Array<{ id: number | string; sub_question_config?: ISubQuestionConfig }>,
): number => {
  const levelDepth = Math.max(0, level - 1);
  const graphDepth = computeDepthFromGraph(questionId, allQuestions);
  if (graphDepth > 0) {
    return Math.max(graphDepth, levelDepth);
  }
  return levelDepth;
};

export const computeDepthFromGraph = (
  questionId: number | string,
  allQuestions: Array<{ id: number | string; sub_question_config?: ISubQuestionConfig }>,
  memo: Map<string, number> = new Map(),
): number => {
  const key = String(questionId);
  if (memo.has(key)) {
    const cached = memo.get(key)!;
    // Cycle detected: this node is already being computed up the call stack.
    // Return 0 so the caller can continue without infinite recursion.
    return cached === DEPTH_COMPUTING ? 0 : cached;
  }

  // Mark as "currently being computed" BEFORE recursing to detect cycles.
  memo.set(key, DEPTH_COMPUTING);

  const parent = findParentInGraph(questionId, allQuestions);
  if (!parent) {
    memo.set(key, 0);
    return 0;
  }

  const depth = Math.min(
    computeDepthFromGraph(parent.id, allQuestions, memo) + 1,
    MAX_LEVEL - 1,
  );
  memo.set(key, depth);
  return depth;
};

const PER_ANSWER_MAPPING_COMPARISONS = ['is', 'in', 'not_is', 'not_in'];

export const applyConditionRuleToAnswerMapping = (
  mapping: Record<string, Array<number | string>>,
  rule: Pick<ISubQuestionCondition, 'comparison' | 'answer_value'>,
  childId: number | string,
): void => {
  if (rule.answer_value === undefined || rule.answer_value === null) {
    return;
  }
  if (!['is', 'in', 'not_is', 'not_in'].includes(rule.comparison)) {
    return;
  }
  normalizeConditionAnswerValues(rule.answer_value).forEach((answerKey) => {
    appendUniqueMappingId(mapping, answerKey, childId);
  });
};

/**
 * Rebuilds per-answer (+) mapping from one child's display_conditions when loading from API.
 * Single-rule children only — compound groups are handled by reconcileExclusiveAnswerMapping.
 */
export const recoverAnswerMappingFromChildConditions = (
  answerMapping: Record<string, Array<number | string>>,
  childId: number | string,
  conditions: ISubQuestionCondition[],
): Record<string, Array<number | string>> => {
  const mapping = { ...answerMapping };

  if (conditions.length !== 1) {
    return mapping;
  }

  applyConditionRuleToAnswerMapping(mapping, conditions[0], childId);
  return mapping;
};

export interface ReconcileAnswerMappingChild {
  id: number | string;
  order?: number;
  display_conditions?: unknown;
}

const displayConditionsKey = (displayConditions: unknown): string =>
  JSON.stringify(displayConditions);

/**
 * Normalizes API answer_mapping payload to frontend Record shape.
 */
export const normalizeAnswerMappingFromApi = (
  answerMapping: unknown,
): Record<string, Array<number | string>> | null => {
  if (!answerMapping || typeof answerMapping !== 'object') {
    return null;
  }

  const normalized: Record<string, Array<number | string>> = {};
  Object.entries(answerMapping as Record<string, unknown>).forEach(([key, ids]) => {
    if (!Array.isArray(ids) || ids.length === 0) {
      return;
    }
    normalized[key] = ids.map((id) => id as number | string);
  });

  return Object.keys(normalized).length > 0 ? normalized : null;
};

/**
 * Hydrates parent answer_mapping on load: API field first, then reconstruct from child display_conditions.
 * [SCOPE] Bug #13: sau Bug #10, child display_conditions có thể null — cần answer_mapping từ API
 * để modal (+) vẫn tick đúng sub-question sau reload.
 * Text/textarea questions never have per-answer mappings — ignore any stale API data.
 */
export const hydrateParentAnswerMappingOnLoad = (
  parentConfig: ISubQuestionConfig,
  children: ReconcileAnswerMappingChild[],
  apiAnswerMapping?: unknown,
  parentType?: string,
): ISubQuestionConfig => {
  const { INPUT, TEXTAREA } = QUESTION_TYPES;
  if (parentType === INPUT || parentType === TEXTAREA) {
    return { ...parentConfig, answer_mapping: {} };
  }

  const fromApi = normalizeAnswerMappingFromApi(apiAnswerMapping);
  if (fromApi) {
    return {
      ...parentConfig,
      answer_mapping: fromApi,
    };
  }

  return reconcileExclusiveAnswerMapping(parentConfig, children);
};

/**
 * Builds answer_mapping for API save — only on parent questions with per-answer assignments.
 * Text/textarea questions never use per-answer mapping (no selectable options), so always null.
 */
export const buildAnswerMappingForApiSave = (
  subQuestionConfig?: ISubQuestionConfig,
  questionType?: string,
): Record<string, Array<number | string>> | null => {
  const { INPUT, TEXTAREA } = QUESTION_TYPES;
  if (questionType === INPUT || questionType === TEXTAREA) {
    return null;
  }
  const mapping = subQuestionConfig?.answer_mapping || {};
  if (Object.keys(mapping).length === 0) {
    return null;
  }
  return { ...mapping };
};

/**
 * Rebuilds answer_mapping after loading all children from API.
 * When multiple children share identical compound display_conditions, assigns one rule per child by order.
 */
export const reconcileExclusiveAnswerMapping = (
  parentConfig: ISubQuestionConfig,
  children: ReconcileAnswerMappingChild[],
): ISubQuestionConfig => {
  const mapping: Record<string, Array<number | string>> = {};
  const groups = new Map<string, ReconcileAnswerMappingChild[]>();

  children.forEach((child) => {
    if (!child.display_conditions) {
      return;
    }
    const key = displayConditionsKey(child.display_conditions);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(child);
  });

  groups.forEach((groupChildren) => {
    const conditions = mapDisplayConditionsFromBackend(groupChildren[0].display_conditions);

    if (groupChildren.length === 1) {
      conditions.forEach((rule) => {
        applyConditionRuleToAnswerMapping(mapping, rule, groupChildren[0].id);
      });
      return;
    }

    if (conditions.length !== groupChildren.length) {
      groupChildren.forEach((child) => {
        const childConditions = mapDisplayConditionsFromBackend(child.display_conditions);
        childConditions.forEach((rule) => {
          applyConditionRuleToAnswerMapping(mapping, rule, child.id);
        });
      });
      return;
    }

    const sortedChildren = [...groupChildren].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    conditions.forEach((rule, index) => {
      applyConditionRuleToAnswerMapping(mapping, rule, sortedChildren[index].id);
    });
  });

  return {
    ...parentConfig,
    answer_mapping: mapping,
  };
};

export interface MergeChildDisplayConditionsParams {
  parentConfig: ISubQuestionConfig;
  parentId: number | string;
  childId: number | string;
  displayConditions: unknown;
}

/**
 * Merges one child's API display_conditions into the parent's sub_question_config (load path).
 */
export const mergeChildDisplayConditionsIntoParentConfig = ({
  parentConfig,
  parentId,
  childId,
  displayConditions,
}: MergeChildDisplayConditionsParams): ISubQuestionConfig => {
  if (!displayConditions) {
    return parentConfig;
  }

  const conditions = mapDisplayConditionsFromBackend(displayConditions);
  const formattedConditions = conditions.map((c) => ({
    ...c,
    target_question_id: parentId,
  }));

  const parentAnswerMapping = parentConfig.answer_mapping || {};

  const mergedConditions = [...(parentConfig.conditions || [])];

  // buildChildDisplayConditions returns null when globalConditions is empty (Bug #10 guard),
  // so any non-null display_conditions reaching this point was explicitly set by the admin
  // and must be merged back. Dedup by (comparison, answer_value) prevents doubles.
  formattedConditions.forEach((fc) => {
    const existingIndex = mergedConditions.findIndex(
      (c) =>
        c.comparison === fc.comparison
        && conditionAnswerValuesMatch(c.answer_value, fc.answer_value),
    );
    if (existingIndex === -1) {
      mergedConditions.push(fc);
    }
    // BUG FIX #2: Do NOT update logical_operator when merging child conditions
    // into parent's global conditions. The parent's logical_operator (AND/OR) is
    // intentionally set by the user and should not be overwritten by child
    // conditions. This prevents the "OR becomes AND" bug when merging children
    // that have matching conditions with different logical_operators.
    // Note: Mapping-only rules are already filtered out at line 846-853, so
    // only global conditions reach this point.
  });

  return {
    ...parentConfig,
    answer_mapping: parentAnswerMapping,
    conditions: mergedConditions,
  };
};

/**
 * Resolves which sub-question IDs are pre-selected when opening the per-answer (+) modal.
 * Global negative conditions (not_in / not_is) must not auto-attach all subs to other answers.
 */
export const resolveInitialSelectedIdsForAnswerTrigger = (
  answerTrigger: string,
  config: Pick<ISubQuestionConfig, 'answer_mapping' | 'conditions' | 'sub_question_ids'>,
): Array<number | string> => {
  const answerMapping = config.answer_mapping || {};
  const fromMapping = answerMapping[answerTrigger];
  if (fromMapping && fromMapping.length > 0) {
    return [...fromMapping];
  }

  // Bug #9: Per-answer (+) mode — unmapped answers must not inherit
  // sub_question_ids from other answers.
  if (Object.keys(answerMapping).length > 0) {
    return [];
  }

  const conditions = config.conditions || [];
  const negativeForAnswer = conditions.some(
    (c) =>
      ['not_in', 'not_is'].includes(c.comparison)
      && normalizeConditionAnswerValues(c.answer_value).includes(String(answerTrigger)),
  );
  if (negativeForAnswer) {
    return [];
  }

  const hasConditionForAnswer = conditions.some((c) => {
    if (PER_ANSWER_MAPPING_COMPARISONS.includes(c.comparison)) {
      return false;
    }
    if (c.comparison === 'is_not_empty') {
      return true;
    }
    if (c.comparison === 'is_empty') {
      return false;
    }
    return true;
  });

  if (hasConditionForAnswer) {
    return [...(config.sub_question_ids || [])];
  }

  return [];
};

export interface QuestionWithSubConfig {
  id: number | string;
  parent_question_id?: number | string | null;
  type?: string;
  display_conditions?: unknown;
  sub_question_config?: ISubQuestionConfig;
}

/**
 * Finds the parent question for a child via sub_question_ids graph or parent_question_id link.
 */
export const findParentQuestionForChild = (
  childId: number | string,
  allQuestions: QuestionWithSubConfig[],
): QuestionWithSubConfig | null => {
  const fromGraph = allQuestions.find((p) =>
    (p.sub_question_config?.sub_question_ids || []).some(
      (sid) => String(sid) === String(childId),
    ),
  );
  if (fromGraph) {
    return fromGraph;
  }

  const child = allQuestions.find((q) => String(q.id) === String(childId));
  if (child?.parent_question_id == null) {
    return null;
  }

  return (
    allQuestions.find((p) => String(p.id) === String(child.parent_question_id)) || null
  );
};

/**
 * Builds API display_conditions for one child (shared by QuestionListEdit save path).
 */
export const buildDisplayConditionsForChildQuestion = (
  child: QuestionWithSubConfig,
  allQuestions: QuestionWithSubConfig[],
): ReturnType<typeof mapDisplayConditionsToBackend> | null => {
  const parent = findParentQuestionForChild(child.id, allQuestions);

  if (parent) {
    const { CHECKBOX } = QUESTION_TYPES;
    const defaultOperator = parent.type === CHECKBOX ? 'in' : 'is';

    return buildChildDisplayConditions({
      childId: child.id,
      globalConditions: parent.sub_question_config?.conditions || [],
      answerMapping: parent.sub_question_config?.answer_mapping || {},
      defaultOperator,
      parentType: parent.type,
    });
  }

  const subIds = child.sub_question_config?.sub_question_ids || [];

  // Bug #7: Parent-level conditions only matter when sub-questions exist.
  // Never fallback to stale child.display_conditions from API. If no subs remain,
  // send null in the payload to prevent orphaned conditions in the database.
  if (
    subIds.length > 0
    && child.sub_question_config?.conditions?.length
    && child.type
    && shouldShowConditionModal(child.type)
  ) {
    const globalLogic = resolveCompoundLogicFromGlobal(child.sub_question_config.conditions);
    // [SCOPE] Bug #11: radio parent modal luôn hiển thị OR (single-select) — ép OR khi
    // build display_conditions của chính câu hỏi cha, giống buildChildDisplayConditions.
    const effectiveLogic: CompoundLogic = child.type === 'radio' ? 'OR' : globalLogic;
    return mapDisplayConditionsToBackend(child.sub_question_config.conditions, {
      logic: effectiveLogic,
    });
  }

  return null;
};

/**
 * Ensures sub_question_ids on parents include children linked via parent_question_id (load/save drift).
 */
export const syncSubQuestionIdsFromParentLinks = <T extends QuestionWithSubConfig>(
  allQuestions: T[],
): T[] => {
  const synced = allQuestions.map((q) => ({
    ...q,
    sub_question_config: {
      ...(q.sub_question_config || createDefaultSubQuestionConfig()),
      sub_question_ids: [
        ...(q.sub_question_config?.sub_question_ids || []),
      ],
      answer_mapping: { ...(q.sub_question_config?.answer_mapping || {}) },
      conditions: [...(q.sub_question_config?.conditions || [])],
    },
  }));

  synced.forEach((child) => {
    if (child.parent_question_id == null) {
      return;
    }
    const parent = synced.find((p) => String(p.id) === String(child.parent_question_id));
    if (!parent?.sub_question_config) {
      return;
    }
    const ids = parent.sub_question_config.sub_question_ids || [];
    if (!ids.some((sid) => String(sid) === String(child.id))) {
      parent.sub_question_config.sub_question_ids = [...ids, child.id];
    }
  });

  return synced;
};

export const stripMappingDuplicateConditions = (
  parentType: string,
  config: ISubQuestionConfig,
): ISubQuestionConfig => {
  if (shouldShowConditionModal(parentType)) {
    return config;
  }
  return {
    ...config,
    conditions: (config.conditions || []).filter(
      (c) => !['is', 'in'].includes(c.comparison),
    ),
  };
};
